import React from 'react';

import { Alert, Button, Card, Col, Empty, Row, Statistic, Tag } from 'antd';

import { FieldState } from '../../../models/FieldState';
import { FieldSummary } from '../../../models/FieldsSummary';

interface Props {
    state: FieldState | null;
    loading: boolean;
    summary?: FieldSummary | null;
    stationNumber?: string | null;
}

const STATUS_META: Record<string, { color: string; label: string }> = {
    ok: { color: 'green', label: 'норма' },
    warn: { color: 'orange', label: 'внимание' },
    none: { color: 'default', label: 'нет данных' },
};

const FieldStateTab = (props: Props) => {
    const { state, loading, summary, stationNumber } = props;

    if (loading) {
        return <Card loading />;
    }
    if (!state) {
        return <Empty description="Нет данных о поле" />;
    }

    const metrics = state.latest?.metrics || {};
    const metricsTime = state.latest?.time ? new Date(state.latest.time * 1000).toLocaleString() : null;

    const productive = summary?.latest?.productive;
    const rain = summary?.rain_24h;
    const status = STATUS_META[summary?.status || 'none'] || STATUS_META.none;

    const pressureMmHg = typeof metrics.pr === 'number' && metrics.pr > 0 ? metrics.pr / 133.322 : null;
    const hasFrost = metrics.is_frost === 1;

    return (
        <>
        {hasFrost && (
            <Alert
                type="error"
                showIcon
                style={{ marginBottom: 12 }}
                message="Заморозок"
                description={`Зафиксирован заморозок${metricsTime ? ' (' + metricsTime + ')' : ''}. Проверьте состояние культуры.`}
            />
        )}
        <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
                <Card title="Атмосфера" size="small" extra={metricsTime ? <span style={{ color: '#8c8c8c', fontSize: 12 }}>{metricsTime}</span> : null}>
                    {!state.latest ? (
                        <Empty description="Данных со станции нет" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    ) : (
                        <Row gutter={[16, 16]}>
                            <Col xs={12} sm={8}>
                                <Statistic
                                    title="Температура"
                                    value={typeof metrics.t === 'number' ? metrics.t : '—'}
                                    suffix="°C"
                                    precision={1}
                                />
                            </Col>
                            <Col xs={12} sm={8}>
                                <Statistic
                                    title="Влажность воздуха"
                                    value={typeof metrics.hm === 'number' ? metrics.hm : '—'}
                                    suffix="%"
                                    precision={0}
                                />
                            </Col>
                            <Col xs={12} sm={8}>
                                <Statistic
                                    title="Давление"
                                    value={pressureMmHg !== null ? pressureMmHg : '—'}
                                    suffix="мм рт. ст."
                                    precision={1}
                                />
                            </Col>
                        </Row>
                    )}
                </Card>
            </Col>

            <Col xs={24} lg={12}>
                <Card
                    title="Осадки 24 ч"
                    size="small"
                    extra={<Tag color={status.color}>{status.label}</Tag>}
                >
                    {rain ? (
                        <Statistic
                            value={rain.value.toFixed(1)}
                            suffix="мм"
                        />
                    ) : (
                        <Empty description="Нет данных об осадках" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )}
                </Card>
            </Col>

            <Col xs={24}>
                <Card title="Влага почвы по глубинам" size="small">
                    {!productive || productive.mm.length === 0 ? (
                        <Empty description="Нет данных о влаге почвы" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    ) : (
                        <Row gutter={[16, 16]}>
                            {productive.mm.map((mm, i) => {
                                const depth = productive.depths_cm[i];
                                const hm = productive.hm[i];
                                return (
                                    <Col key={i} xs={12} sm={8} md={6}>
                                        <Statistic
                                            title={`${depth ?? '?'} см`}
                                            value={typeof mm === 'number' ? mm.toFixed(0) : '—'}
                                            suffix="мм"
                                        />
                                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                                            {typeof hm === 'number' ? `${hm.toFixed(0)}% продуктивной` : ''}
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                    )}
                </Card>
            </Col>

            {state.zonds.length === 0 && (
                <Col xs={24}>
                    <Card size="small" style={{ borderStyle: 'dashed' }}>
                        <div style={{ textAlign: 'center', padding: '8px 0' }}>
                            <div style={{ fontWeight: 600, marginBottom: 6 }}>На поле нет станции</div>
                            <div style={{ color: '#8c8c8c', fontSize: 13, maxWidth: 560, margin: '0 auto 12px', lineHeight: 1.6 }}>
                                Станция на поле измеряет влажность и температуру почвы на разных глубинах — там, где
                                развивается корневая система культуры. Это не средние метеоданные по району, а точные
                                показатели вашего поля: достаточно ли влаги, прогрелась ли почва для сева.
                            </div>
                            <Button type="primary">Как получить станцию</Button>
                        </div>
                    </Card>
                </Col>
            )}

            <Col xs={24}>
                <div style={{ fontSize: 11, color: '#8c8c8c', textAlign: 'right', marginTop: 8 }}>
                    Источник данных: {stationNumber ? `Станция ${stationNumber}` : 'Open-Meteo'}
                </div>
            </Col>
        </Row>
        </>
    );
};

export default FieldStateTab;
